import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useSearchParams } from 'react-router-dom';
import AddKitDialog from '../components/AddKitDialog';
import { Package, Plus, ChevronRight, ImageOff, X, Pencil, LayoutGrid, List, CheckCircle2, CircleDot, Printer, Search } from 'lucide-react';
import { toast } from 'sonner';

const VIEW_LS_KEY = 'wgi_kits_view';

function KitStatusBadge({ status, blocked, total }) {
  if (!total) return null;
  if (status === 'available')
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
        title="All contained items are available"
        data-testid="kit-status-available"
      >
        <CheckCircle2 size={10} /> Available
      </span>
    );
  if (status === 'checked-out')
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20"
        title={`${blocked} of ${total} items fully checked out`}
        data-testid="kit-status-checked-out"
      >
        <CircleDot size={10} /> Unavailable
      </span>
    );
  if (status === 'partial')
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20"
        title={`${blocked} of ${total} items partially packed`}
        data-testid="kit-status-partial"
      >
        <CircleDot size={10} /> Partial
      </span>
    );
  if (status === 'reserved')
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-yellow-400/10 text-yellow-300 border border-yellow-400/30"
        title={`${blocked} of ${total} items reserved on an active project`}
        data-testid="kit-status-reserved"
      >
        <CircleDot size={10} /> Reserved
      </span>
    );
  return null;
}

function KitCard({ kit, onOpen, onEdit, onDelete }) {
  const [err, setErr] = useState(false);
  const { kitStatus } = useApp();
  const ks = kitStatus(kit);
  return (
    <div className="surface rounded-lg overflow-hidden flex flex-col group hover:border-amber-500/40 transition-colors relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        title="Edit kit"
        className="absolute top-2 right-[4.75rem] z-10 h-7 w-7 rounded-md bg-neutral-950/80 border border-neutral-800 text-neutral-400 hover:text-amber-400 hover:border-amber-500/40 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Pencil size={11} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          window.open(`/print-label/kit/${kit.code}`, '_blank', 'width=540,height=620');
        }}
        title="Print QR label"
        data-testid={`print-label-kit-${kit.code}`}
        className="absolute top-2 right-10 z-10 h-7 w-7 rounded-md bg-neutral-950/80 border border-neutral-800 text-neutral-400 hover:text-amber-400 hover:border-amber-500/40 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Printer size={11} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        title="Delete kit"
        className="absolute top-2 right-2 z-10 h-7 w-7 rounded-md bg-neutral-950/80 border border-neutral-800 text-neutral-400 hover:text-rose-400 hover:border-rose-500/40 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={12} />
      </button>
      <button onClick={onOpen} className="text-left flex flex-col flex-1">
        <div className="h-36 bg-neutral-950 relative overflow-hidden">
          {!err && kit.image ? (
            <img
              src={kit.image}
              alt={kit.name}
              onError={() => setErr(true)}
              className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-neutral-700">
              <ImageOff size={28} />
            </div>
          )}
          <div className="absolute top-2 left-2 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded bg-neutral-950/80 border border-neutral-800 text-neutral-300 font-mono">
            {kit.code}
          </div>
          <div className="absolute bottom-2 left-2">
            <KitStatusBadge status={ks.status} blocked={ks.blocked} total={ks.total} />
          </div>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-start gap-2 mb-1">
            <Package size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <h3 className="text-sm font-semibold text-white">{kit.name}</h3>
          </div>
          <p className="text-xs text-neutral-400 flex-1 mb-3 line-clamp-2">{kit.description}</p>
          <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">
            {Array.from(new Set(kit.itemCodes)).length} items
            {kit.itemCodes.length !== new Set(kit.itemCodes).size && (
              <span className="text-neutral-600"> · {kit.itemCodes.length} units</span>
            )}
          </div>
          <div className="flex items-center gap-1 mb-3 flex-wrap">
            {Array.from(new Set(kit.itemCodes)).slice(0, 6).map((c) => (
              <span
                key={c}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-900 border border-neutral-800 text-neutral-400"
              >
                {c}
              </span>
            ))}
            {new Set(kit.itemCodes).size > 6 && (
              <span className="text-[10px] text-neutral-500">+{new Set(kit.itemCodes).size - 6}</span>
            )}
          </div>
          <div className="flex items-center justify-end text-xs text-amber-400 group-hover:text-amber-300">
            View kit <ChevronRight size={12} />
          </div>
        </div>
      </button>
    </div>
  );
}

function KitDetail({ kit, onClose, onEdit }) {
  const { items, categoryColor } = useApp();
  if (!kit) return null;
  // Count occurrences in flat itemCodes → qty per code, preserving first-occurrence order
  const qtyMap = {};
  const order = [];
  (kit.itemCodes || []).forEach((c) => {
    if (qtyMap[c] == null) {
      qtyMap[c] = 0;
      order.push(c);
    }
    qtyMap[c] += 1;
  });
  const kitItems = order
    .map((c) => ({ item: items.find((i) => i.code === c), qty: qtyMap[c] }))
    .filter((x) => x.item);
  const totalUnits = Object.values(qtyMap).reduce((s, n) => s + n, 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="surface rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-neutral-900">
          <div>
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">
              Kit · {kit.code}
            </div>
            <h2 className="text-lg font-bold text-white">{kit.name}</h2>
            <p className="text-xs text-neutral-400 mt-1">{kit.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="text-xs px-2.5 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 flex items-center gap-1.5"
            >
              <Pencil size={11} /> Edit
            </button>
            <button onClick={onClose} className="text-neutral-500 hover:text-white p-1">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-5">
          <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-2">
            Gear contained ({kitItems.length} item{kitItems.length === 1 ? '' : 's'} · {totalUnits} unit{totalUnits === 1 ? '' : 's'})
          </div>
          <div className="space-y-2">
            {kitItems.map(({ item: it, qty }) => (
              <div
                key={it.code}
                className="flex items-center gap-3 p-2.5 rounded-md border border-neutral-900 bg-neutral-950"
              >
                <span className="cat-dot" style={{ background: categoryColor(it.category) }} />
                <span className="text-[11px] font-mono text-neutral-400 w-16">{it.code}</span>
                <span className="text-sm text-white flex-1">{it.name}</span>
                {qty > 1 && (
                  <span className="text-[11px] font-mono text-amber-300 font-semibold">×{qty}</span>
                )}
                <span className="text-[11px] text-neutral-500">{it.category}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KitListRow({ kit, onOpen, onEdit, onDelete }) {
  const { kitStatus } = useApp();
  const ks = kitStatus(kit);
  const uniqueCodes = Array.from(new Set(kit.itemCodes || []));
  const totalUnits = (kit.itemCodes || []).length;
  return (
    <div className="grid grid-cols-[100px_minmax(180px,1fr)_minmax(200px,2fr)_110px_80px_120px] row-hover border-b border-neutral-900 group cursor-pointer" onClick={onOpen}>
      <div className="cell text-neutral-300 font-mono text-[12px] flex items-center gap-2">
        <Package size={12} className="text-amber-400 shrink-0" />
        {kit.code}
      </div>
      <div className="cell text-white">{kit.name}</div>
      <div className="cell text-neutral-400 text-[12px] truncate">{kit.description || <span className="text-neutral-700">—</span>}</div>
      <div className="cell">
        <KitStatusBadge status={ks.status} blocked={ks.blocked} total={ks.total} />
      </div>
      <div className="cell text-right text-neutral-200">
        <span className="text-white font-medium">{uniqueCodes.length}</span>
        <span className="text-neutral-600"> items</span>
        {totalUnits !== uniqueCodes.length && (
          <span className="text-neutral-600"> · {totalUnits} units</span>
        )}
      </div>
      <div className="cell text-right" onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onEdit}
            title="Edit kit"
            className="h-6 w-6 grid place-items-center rounded text-neutral-400 hover:bg-neutral-900 hover:text-amber-400"
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/print-label/kit/${kit.code}`, '_blank', 'width=540,height=620');
            }}
            title="Print QR label"
            data-testid={`print-label-kit-row-${kit.code}`}
            className="h-6 w-6 grid place-items-center rounded text-neutral-400 hover:bg-neutral-900 hover:text-amber-400"
          >
            <Printer size={11} />
          </button>
          <button
            onClick={onDelete}
            title="Delete kit"
            className="h-6 w-6 grid place-items-center rounded text-neutral-400 hover:bg-neutral-900 hover:text-rose-400"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Kits() {
  const { kits, items, deleteKit } = useApp();
  const [open, setOpen] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get('q') || '';
  const [localQ, setLocalQ] = useState(urlQ);
  const search = (localQ || urlQ || '').toLowerCase().trim();

  // Build an item-code -> item-name lookup so we can match kits by the names of
  // the gear they contain, not just the literal codes.
  const itemNameByCode = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      m[i.code] = (i.name || '').toLowerCase();
    });
    return m;
  }, [items]);

  const filteredKits = useMemo(() => {
    if (!search) return kits;
    return kits.filter((k) => {
      const hay = [
        k.code,
        k.name,
        k.description,
        ...(k.itemCodes || []),
        ...(k.itemCodes || []).map((c) => itemNameByCode[c] || ''),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(search);
    });
  }, [kits, search, itemNameByCode]);

  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_LS_KEY) || 'cards';
    } catch {
      return 'cards';
    }
  });

  const setViewPersist = (v) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_LS_KEY, v);
    } catch {
      /* noop */
    }
  };

  const handleDelete = async (kit) => {
    if (!window.confirm(`Delete kit "${kit.name}"? This cannot be undone.`)) return;
    try {
      await deleteKit(kit.code);
      toast.success('Kit deleted');
    } catch (e) {
      toast.error(e.message || 'Could not delete');
    }
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white">Kit Inventory</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            {search ? (
              <>
                {filteredKits.length} of {kits.length} kit{kits.length === 1 ? '' : 's'} match &quot;{search}&quot;
              </>
            ) : (
              <>
                {kits.length} kit{kits.length === 1 ? '' : 's'} · Pre-built gear bundles. Add a kit to a project and every item inside is packed/checked-in together.
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={localQ}
              onChange={(e) => {
                setLocalQ(e.target.value);
                if (e.target.value) setSearchParams({ q: e.target.value });
                else setSearchParams({});
              }}
              placeholder="Filter kits…"
              className="bg-neutral-950 border border-neutral-800 text-white text-xs rounded-md pl-8 pr-7 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60 w-48"
              data-testid="kits-filter-input"
            />
            {localQ && (
              <button
                onClick={() => {
                  setLocalQ('');
                  setSearchParams({});
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                data-testid="kits-filter-clear"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <div className="inline-flex rounded-md border border-neutral-800 p-0.5 bg-neutral-950">
            <button
              onClick={() => setViewPersist('cards')}
              title="Card view"
              className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 transition-colors ${
                view === 'cards'
                  ? 'bg-amber-500/15 text-amber-300'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <LayoutGrid size={12} /> Cards
            </button>
            <button
              onClick={() => setViewPersist('list')}
              title="List view"
              className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 transition-colors ${
                view === 'list'
                  ? 'bg-amber-500/15 text-amber-300'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <List size={12} /> List
            </button>
          </div>
          <button
            onClick={() => {
              setEditingKit(null);
              setAddOpen(true);
            }}
            className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-2"
          >
            <Plus size={14} /> New Kit
          </button>
        </div>
      </div>

      {view === 'cards' ? (
        filteredKits.length === 0 ? (
          <div className="surface rounded-lg p-10 text-center text-sm text-neutral-500" data-testid="kits-empty-filtered">
            {search ? <>No kits match &quot;{search}&quot;.</> : 'No kits yet.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredKits.map((k) => (
              <KitCard
                key={k.id}
                kit={k}
                onOpen={() => setOpen(k)}
                onEdit={() => {
                  setEditingKit(k);
                  setAddOpen(true);
                }}
                onDelete={() => handleDelete(k)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="surface rounded-lg overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="min-w-[790px]">
          <div className="grid grid-cols-[100px_minmax(180px,1fr)_minmax(200px,2fr)_110px_80px_120px]">
            <div className="head-cell">Code</div>
            <div className="head-cell">Name</div>
            <div className="head-cell">Description</div>
            <div className="head-cell">Status</div>
            <div className="head-cell text-right">Items</div>
            <div className="head-cell text-right">Actions</div>
          </div>
          {filteredKits.length === 0 ? (
            <div className="p-10 text-center text-sm text-neutral-500" data-testid="kits-empty-filtered">
              {search ? <>No kits match &quot;{search}&quot;.</> : 'No kits yet.'}
            </div>
          ) : (
            filteredKits.map((k) => (
              <KitListRow
                key={k.id}
                kit={k}
                onOpen={() => setOpen(k)}
                onEdit={() => {
                  setEditingKit(k);
                  setAddOpen(true);
                }}
                onDelete={() => handleDelete(k)}
              />
            ))
          )}
          </div>
        </div>
      )}

      <KitDetail
        kit={open}
        onClose={() => setOpen(null)}
        onEdit={() => {
          setEditingKit(open);
          setOpen(null);
          setAddOpen(true);
        }}
      />
      <AddKitDialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setEditingKit(null);
        }}
        editingKit={editingKit}
      />
    </div>
  );
}
